import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable, from, of, throwError } from 'rxjs';
import { concatMap, mergeMap, toArray, timeout, catchError } from 'rxjs/operators';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Pagination } from '../../models/response.model';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { CasingFitment } from '../../models/casing-fitment.model';
import { DealerState } from '../../../../shared/state/DealerState';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-casing-fitment',
  templateUrl: './casing-fitment.component.html',
  styleUrl: './casing-fitment.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingFitmentComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingFitment>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);

  seriesList: string[] = [];
  sizeList: string[] = [];
  treadsList: string[] = [];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;

  columnNames: Record<string, string> = {};
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  private dialog = inject(MatDialog);

  casingFitments: CasingFitment[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;

  tableConfig: TableConfig<CasingFitment> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  exportDataFetcher: () => Observable<ExportResponse> = () => {
    const pageSize = 1000;
    const filters = this.filterFormBackend.value;

    return this.productDataService.getCasingFitments(filters, 1, pageSize).pipe(
      concatMap((firstResp: any) => {
        const total = firstResp?.data?.pagination?.totalRecords ?? ((firstResp.data.casingFitments || []).length || 0);
        const cappedTotal = Math.min(total, 10000);
        const totalPages = Math.max(1, Math.ceil(cappedTotal / pageSize));

        const pageObservables = [] as Observable<any>[];
        pageObservables.push(of(firstResp));
        for (let p = 2; p <= totalPages; p++) {
          pageObservables.push(this.productDataService.getCasingFitments(filters, p, pageSize));
        }

        return from(pageObservables).pipe(
          mergeMap((obs) => obs, 3),
          map((r: any) => (r?.data?.casingFitments ?? []) as CasingFitment[]),
          toArray(),
          map((chunks: CasingFitment[][]) => ([] as CasingFitment[]).concat(...chunks)),
          map((all: CasingFitment[]) => ({
            data: all
              .slice(0, cappedTotal)
              .sort((a, b) => (a.size ?? '').localeCompare(b.size ?? '', undefined, { numeric: true, sensitivity: 'base' }))
              .map((item) => ({
                make: item.make,
                series: item.series,
                size: item.size,
                cureType: item.cureType,
                tread: item.tread
              }))
          }))
        );
      }),
      timeout(30000),
      catchError((err) => {
        if (err && err.name === 'TimeoutError') {
          console.error('Export timed out after 30 seconds', err);
          return throwError(() => new Error('Export timed out after 30 seconds'));
        }
        console.error('Export failed', err);
        return throwError(() => err);
      })
    );
  };

  fetcher: PaginatorFetcher<CasingFitment> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getCasingFitments({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.casingFitments = response.data.casingFitments || [];
        this.pagination = response.data.pagination;
        return { items: this.casingFitments, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      series: [[]],
      size: [[]],
      treads: [[]]
    });
    this.filterFormBackend = this.fb.group({
      series: [[]],
      size: [[]],
      treads: [[]]
    });
  }

  ngOnInit(): void {
    this.seriesList = this.store.selectSnapshot(ProductsState.products).series;
    this.sizeList = this.store.selectSnapshot(ProductsState.products).sizes;
    this.treadsList = this.store.selectSnapshot(ProductsState.products).allTreads;
    this.filterForm.patchValue({
      series: [],
      size: [],
      treads: []
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        size: { cell: (row) => row.size, header: this.translationService.translate('sizeText') },
        series: { cell: (row) => row.series, header: this.translationService.translate('seriesText') },
        cureType: { cell: (row) => row.cureType, header: this.translationService.translate('cureTypeText') },
        treads: { cell: (row) => row.tread, header: this.translationService.translate('treadText') },
        make: { cell: (row) => row.make, header: this.translationService.translate('makeText') }
      };
      this.columnNames = {
        size: this.translationService.translate('sizeText'),
        series: this.translationService.translate('seriesText'),
        cureType: this.translationService.translate('cureTypeText'),
        tread: this.translationService.translate('treadText'),
        make: this.translationService.translate('makeText')
      };
      this.cdr.markForCheck();
    });
  }

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-CasingFitment-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  clearFilters(): void {
    this.filterForm.reset({
      series: [],
      size: [],
      treads: []
    });
  }

  private generateFilterForm(): void {
    const filterSeries = this.filterForm.get('series').value;
    const filterSize = this.filterForm.get('size').value;
    const filterTread = this.filterForm.get('treads').value;

    this.filterFormBackend.patchValue({
      series: filterSeries.length > 0 && filterSeries.length === this.seriesList.length ? [] : filterSeries,
      size: filterSize.length > 0 && filterSize.length === this.sizeList.length ? [] : filterSize,
      treads: filterTread.length > 0 && filterTread.length === this.treadsList.length ? [] : filterTread
    });
  }
}
